import { useState } from "react";
import { Modal } from "../../common/components/Modal";

interface UpgradeModalProps {
    isOpen: boolean;
    onClose: () => void;
    projectName?: string;
    projectLocation?: string;
    projectCapacity?: number;
}

export function UpgradeModal({
    isOpen,
    onClose,
    projectName,
    projectLocation,
    projectCapacity,
}: UpgradeModalProps) {
    const [submitted, setSubmitted] = useState(false);

    if (!isOpen) return null;

    const subject = encodeURIComponent(
        `Full Analysis Request${projectName ? `: ${projectName}` : ""}`
    );
    const body = encodeURIComponent(
        [
            "I'd like to request full LCA results access.",
            "",
            projectName ? `Project: ${projectName}` : "",
            projectLocation ? `Location: ${projectLocation}` : "",
            projectCapacity ? `Capacity: ${projectCapacity} MW` : "",
            "",
            "Please contact me to discuss engagement options.",
        ]
            .filter(Boolean)
            .join("\n")
    );

    const mailtoUrl = `mailto:sales@totalview.energy?subject=${subject}&body=${body}`;

    const handleSubmit = () => {
        window.open(mailtoUrl, "_blank");
        setSubmitted(true);
    };

    return (
        <Modal title="Unlock Full Results" onClose={onClose}>
            <div className="p-6 space-y-6">
                {submitted ? (
                    <div className="text-center py-4">
                        <div className="mx-auto w-16 h-16 rounded-full bg-ecosystem-500/10 flex items-center justify-center mb-4">
                            <svg className="w-8 h-8 text-ecosystem-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-text-primary mb-2">Request Sent</h3>
                        <p className="text-body-sm text-text-secondary">
                            Our team will reach out within 1 business day to discuss your analysis needs.
                        </p>
                        <button onClick={onClose} className="btn-primary mt-6 px-6 py-2">
                            Done
                        </button>
                    </div>
                ) : (
                    <>
                        <p className="text-body-sm text-text-secondary">
                            Your screening results show the three headline impact metrics. Upgrade to a full TotalView engagement to unlock:
                        </p>

                        <ul className="space-y-3">
                            {[
                                "Detailed impact category breakdowns (18+ midpoint indicators)",
                                "Hourly time-series impact data and visualizations",
                                "ISO 14044-compliant reporting and compliance documentation",
                                "Excel and CSV data exports for further analysis",
                                "Dedicated support from our LCA engineering team",
                            ].map((item, i) => (
                                <li key={i} className="flex items-start gap-3 text-body-sm text-text-primary">
                                    <svg className="w-5 h-5 text-ecosystem-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    {item}
                                </li>
                            ))}
                        </ul>

                        {projectName && (
                            <div className="bg-surface-700 rounded-lg p-4 border border-border">
                                <h4 className="text-caption font-medium text-text-muted uppercase tracking-wider mb-2">
                                    Project Details
                                </h4>
                                <div className="space-y-1 text-body-sm">
                                    <p className="text-text-primary font-medium">{projectName}</p>
                                    {projectLocation && (
                                        <p className="text-text-secondary">{projectLocation}</p>
                                    )}
                                    {projectCapacity && (
                                        <p className="text-text-secondary">{projectCapacity} MW</p>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="flex gap-3 pt-2">
                            <button onClick={onClose} className="btn-ghost flex-1 py-2.5">
                                Maybe Later
                            </button>
                            <button onClick={handleSubmit} className="btn-primary flex-1 py-2.5">
                                Request Full Analysis
                            </button>
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
}
